import {isImageRefSet} from '@packages/systems/dynamo/utils/CollectionFieldUtils';

import {
  getBindableFields,
  defineBindablePropertyFromField,
  toBindingPath,
  defineCollectionTypeReference,
  sortFields,
} from '@packages/systems/cms/gateway/shared';

import {mifBindingContextTypeId} from './identifiers';

import type {
  Collections,
  CollectionRecordType as Collection,
} from '@packages/systems/dynamo/records/CollectionRecord';
import type {
  BindablePropertiesDef,
  BindablePropertyDef,
  TypeReferenceBuilder,
} from '@packages/systems/dynamo/source-types';

export default function defineBindableProperties<
  RootTRef,
  EntityTRef,
  ListTRef,
>(
  collections: Collections,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>,
  isDesigner: boolean
): BindablePropertiesDef<EntityTRef, ListTRef>[] {
  return collections.reduce<BindablePropertiesDef<EntityTRef, ListTRef>[]>(
    (propsDefs, collection) => {
      if (collection._id) {
        propsDefs.push(
          defineCollectionBindableProperties(
            collection,
            referenceBuilder,
            isDesigner
          ),
          ...defineMultiImageFieldBindableProperties(
            collection,
            referenceBuilder
          )
        );
      }
      return propsDefs;
    },
    []
  );
}

function defineCollectionBindableProperties<RootTRef, EntityTRef, ListTRef>(
  collection: Collection,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>,
  isDesigner: boolean
): BindablePropertiesDef<EntityTRef, ListTRef> {
  let bindableFields = getBindableFields(collection);
  if (isDesigner) {
    // sort fields only in Designer environment for UI reasons
    bindableFields = sortFields(bindableFields);
  }
  const bindableProperties = bindableFields.reduce<
    BindablePropertyDef<EntityTRef, ListTRef>[]
  >((bindableProps, field) => {
    const bindableProp = defineBindablePropertyFromField<
      RootTRef,
      EntityTRef,
      ListTRef
    >({collection, field, referenceBuilder});

    if (bindableProp) {
      bindableProps.push(bindableProp);
    }

    return bindableProps;
  }, []);

  bindableProperties.push({
    id: 'full-slug',
    type: 'FullSlug',
    label: 'URL Path',
    path: [{slug: 'fullSlug', type: 'FullSlug'}],
    queryPath: ['fullSlug'],
    meta: {
      required: false,
    },
  });

  bindableProperties.push({
    id: 'no-search',
    type: 'Bool',
    label: 'NoSearch',
    // this is the binding path we need for a binding
    path: [{slug: 'noSearch', type: 'Bool'}],
    // this needs to match the field name in the GraphQL schema
    queryPath: ['noSearch'],
    // !IMPORTANT! noSearch is used only in query generation and
    // and should not be available as a binding option, so we mark
    // it as "deprecated" even though it is not deprecated
    deprecated: true,
    meta: {
      required: false,
    },
  });

  return {
    type: referenceBuilder.entityOf(defineCollectionTypeReference(collection)),
    bindableProperties,
  };
}

function defineMultiImageFieldBindableProperties<
  RootTRef,
  EntityTRef,
  ListTRef,
>(
  collection: Collection,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindablePropertiesDef<EntityTRef, ListTRef>[] {
  return collection.fields.reduce<
    BindablePropertiesDef<EntityTRef, ListTRef>[]
  >((bindableProps, field) => {
    if (isImageRefSet(field)) {
      bindableProps.push({
        // TODO: Address field id uniqueness
        type: referenceBuilder.entityOf({
          entity: 'Field',
          id: mifBindingContextTypeId(field.id),
        }),
        bindableProperties: [
          {
            id: field.id,
            type: 'ImageRef',
            label: field.name,
            path: toBindingPath([field]),
            queryPath: [],
            meta: {
              required: field.required,
            },
          },
        ],
      });
    }

    return bindableProps;
  }, []);
}
